import type { Tenant } from '~/types/models';

function normalizeHost(value: string): string {
    return value.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function hasVerifiedTenantCustomDomain(tenant?: Tenant | null): boolean {
    return Boolean(
        tenant?.domainVerified
        && normalizeHost(String(tenant?.customDomain || '')),
    );
}

export function canUseTenantBrandedEmailLinks(tenant?: Tenant | null): boolean {
    const emailSettings = tenant?.emailSettings || {};
    const domainName = normalizeHost(String(emailSettings.domainName || '').toLowerCase());
    const domainStatus = String(emailSettings.domainStatus || '').trim().toLowerCase();
    const fromEmail = String(emailSettings.fromEmail || '').trim().toLowerCase();

    return Boolean(
        hasVerifiedTenantCustomDomain(tenant)
        && emailSettings.whiteLabelEnabled
        && domainName
        && fromEmail
        && domainStatus === 'verified'
        && fromEmail.endsWith(`@${domainName}`),
    );
}

export function resolveTenantOrigin(options: {
    tenant?: Pick<Tenant, 'customDomain' | 'domainVerified'> | null;
    appDomain?: string;
    fallbackOrigin: string;
}): string {
    const tenantCustomDomain = normalizeHost(String(options.tenant?.customDomain || ''));
    const tenantDomainVerified = Boolean(options.tenant?.domainVerified);
    const configuredAppDomain = normalizeHost(String(options.appDomain || ''));
    const preferredHost = tenantDomainVerified && tenantCustomDomain
        ? tenantCustomDomain
        : configuredAppDomain;

    if (!preferredHost) {
        return options.fallbackOrigin;
    }

    return `https://${preferredHost}`;
}

export function resolveWorkspaceOrigin(options: {
    appDomain?: string;
    fallbackOrigin: string;
}): string {
    const configuredAppDomain = normalizeHost(String(options.appDomain || ''));
    if (!configuredAppDomain) {
        return options.fallbackOrigin;
    }

    return `https://${configuredAppDomain}`;
}

export function buildTenantWorkspaceUrl(options: {
    origin: string;
    tenantSlug?: string | null;
    path?: string;
}): string {
    const tenantSlug = String(options.tenantSlug || '').trim();
    const rawPath = String(options.path || '/').trim() || '/';
    const normalizedPath = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;

    let tenantPath = normalizedPath;
    if (tenantSlug && normalizedPath !== `/${tenantSlug}` && !normalizedPath.startsWith(`/${tenantSlug}/`)) {
        tenantPath = `/${tenantSlug}${normalizedPath === '/' ? '' : normalizedPath}`;
    }

    return `${options.origin.replace(/\/$/, '')}${tenantPath}`;
}

export function resolveClientEmailOrigin(options: {
    tenant?: Tenant | null;
    appDomain?: string;
    fallbackOrigin: string;
}): string {
    const configuredAppDomain = normalizeHost(String(options.appDomain || ''));
    const preferredHost = canUseTenantBrandedEmailLinks(options.tenant)
        ? normalizeHost(String(options.tenant?.customDomain || ''))
        : configuredAppDomain;

    if (!preferredHost) {
        return options.fallbackOrigin;
    }

    return `https://${preferredHost}`;
}

export type ClientPortalSection = 'approvals' | 'tasks' | 'quotes' | 'invoices' | 'projects' | 'forms' | 'messages';

export type ClientPortalRouteOptions = {
    clientSlug?: string;
    tenantId?: string;
    section?: ClientPortalSection;
    resourceId?: string;
    focusParam?: string;
    focusValue?: string;
};

export function buildClientPortalPath(options: ClientPortalRouteOptions): string {
    const clientSlug = String(options.clientSlug || '').trim().toLowerCase();
    if (!clientSlug) {
        return '';
    }

    const sectionPath = options.section && options.resourceId
        ? `/${options.section}/${encodeURIComponent(options.resourceId)}`
        : options.section
            ? `/${options.section}`
            : '';

    const params = new URLSearchParams();
    const tenantId = String(options.tenantId || '').trim();
    if (tenantId) {
        params.set('tenantId', tenantId);
    }

    if (options.focusParam && options.focusValue) {
        params.set(options.focusParam, options.focusValue);
    }

    const query = params.toString();
    return `/portal/${encodeURIComponent(clientSlug)}${sectionPath}${query ? `?${query}` : ''}`;
}

export function buildClientPortalLink(options: ClientPortalRouteOptions & { origin: string }): string {
    const path = buildClientPortalPath(options);
    if (!path) {
        return options.origin;
    }

    return new URL(path, `${options.origin.replace(/\/$/, '')}/`).toString();
}

export function buildClientPortalDestination(options: ClientPortalRouteOptions & { origin: string }): {
    actionLink: string;
    actionPath: string;
} {
    return {
        actionLink: buildClientPortalLink(options),
        actionPath: buildClientPortalPath(options),
    };
}

export function buildPublicQuoteLink(options: {
    origin: string;
    token?: string | null;
    tenantId?: string | null;
    quoteId?: string | null;
}): string {
    const token = String(options.token || '').trim();
    if (!token) {
        return options.origin;
    }

    const url = new URL(`${options.origin.replace(/\/$/, '')}/public/quotes/${encodeURIComponent(token)}`);
    const tenantId = String(options.tenantId || '').trim();
    const quoteId = String(options.quoteId || '').trim();
    if (tenantId) {
        url.searchParams.set('tenantId', tenantId);
    }
    if (quoteId) {
        url.searchParams.set('quoteId', quoteId);
    }

    return url.toString();
}

export function buildPublicProposalLink(options: {
    origin: string;
    token?: string | null;
    tenantId?: string | null;
    proposalId?: string | null;
}): string {
    const token = String(options.token || '').trim();
    if (!token) return options.origin;

    const url = new URL(`${options.origin.replace(/\/$/, '')}/public/proposals/${encodeURIComponent(token)}`);
    const tenantId = String(options.tenantId || '').trim();
    const proposalId = String(options.proposalId || '').trim();
    if (tenantId) url.searchParams.set('tenantId', tenantId);
    if (proposalId) url.searchParams.set('proposalId', proposalId);
    return url.toString();
}

export function buildPublicInvoiceLink(options: {
    origin: string;
    token?: string | null;
    tenantId?: string | null;
    invoiceId?: string | null;
}): string {
    const token = String(options.token || '').trim();
    if (!token) {
        return options.origin;
    }

    const url = new URL(`${options.origin.replace(/\/$/, '')}/public/invoices/${encodeURIComponent(token)}`);
    const tenantId = String(options.tenantId || '').trim();
    const invoiceId = String(options.invoiceId || '').trim();
    if (tenantId) {
        url.searchParams.set('tenantId', tenantId);
    }
    if (invoiceId) {
        url.searchParams.set('invoiceId', invoiceId);
    }

    return url.toString();
}

export function buildPublicContractLink(options: {
    origin: string;
    token?: string | null;
    tenantId?: string | null;
    clientId?: string | null;
    projectId?: string | null;
}): string {
    const token = String(options.token || '').trim();
    if (!token) {
        return options.origin;
    }

    const url = new URL(`${options.origin.replace(/\/$/, '')}/public/contracts/${encodeURIComponent(token)}`);
    const tenantId = String(options.tenantId || '').trim();
    const clientId = String(options.clientId || '').trim();
    const projectId = String(options.projectId || '').trim();

    if (tenantId) {
        url.searchParams.set('tenantId', tenantId);
    }
    if (clientId) {
        url.searchParams.set('clientId', clientId);
    }
    if (projectId) {
        url.searchParams.set('projectId', projectId);
    }

    return url.toString();
}
